// The pure side of the subtitle maker: turning a transcript into timed cues, editing them, and writing SRT / VTT / text files. No drawing and no network here.
//
// A cue is { id, start, end, text, alt } - times in seconds, `alt` the translation (or "" when there is none).

export const DEFAULTS = { maxChars: 42, maxLines: 2, minSeconds: 0.9, maxSeconds: 7, maxCps: 21, gap: 0.04 };

const SENTENCE_END = /[.!?…؟。！？।۔]["')\]»”’]*$/;
const SOFT_END = /[,;:،؛、，]["')\]»”’]*$/;
const length = (text) => [...text].length;                                       // characters, not code units (Urdu, emoji)
export const wordsOf = (text) => String(text || "").split(/\s+/).filter(Boolean);

let nextId = 1;
export const newId = () => { nextId += 1; return nextId; };

// ------------------------------------------------------------------------------------------ text -> cue-sized pieces
// Cuts a transcript into pieces that each fit a cue (about maxChars x maxLines characters), at sentence ends where it can, then at commas, else at the limit.
export function splitText(text, { maxChars = DEFAULTS.maxChars, maxLines = DEFAULTS.maxLines } = {}) {
  const limit = maxChars * maxLines;
  const out = [];
  let current = [];
  let size = 0;
  const flush = () => { if (current.length) out.push(current.join(" ")); current = []; size = 0; };
  wordsOf(text).forEach((word) => {
    const add = length(word) + (current.length ? 1 : 0);
    if (current.length && size + add > limit) flush();
    size += length(word) + (current.length ? 1 : 0);
    current.push(word);
    if (SENTENCE_END.test(word) && size >= 12) flush();
    else if (SOFT_END.test(word) && size >= limit * 0.6) flush();
  });
  flush();
  // a very short last piece is joined to the one before it when they fit together
  for (let i = out.length - 1; i > 0; i -= 1) {
    if (length(out[i]) < 9 && length(out[i - 1]) + 1 + length(out[i]) <= limit) { out[i - 1] = `${out[i - 1]} ${out[i]}`; out.splice(i, 1); }
  }
  return out;
}

// A cue's text broken into at most `maxLines` lines, as even as can be (a pyramid of short-then-long is avoided).
export function wrapLines(text, maxChars = DEFAULTS.maxChars, maxLines = DEFAULTS.maxLines) {
  const ws = wordsOf(text);
  const whole = ws.join(" ");
  if (length(whole) <= maxChars || maxLines < 2 || ws.length < 2) return [whole];
  const lines = Math.min(maxLines, Math.ceil(length(whole) / maxChars));
  if (lines === 2) {
    let best = 1;
    let bestScore = Infinity;
    for (let i = 1; i < ws.length; i += 1) {
      const a = length(ws.slice(0, i).join(" "));
      const b = length(ws.slice(i).join(" "));
      const score = Math.abs(a - b) + (Math.max(a, b) > maxChars ? 1000 : 0);
      if (score < bestScore) { bestScore = score; best = i; }
    }
    return [ws.slice(0, best).join(" "), ws.slice(best).join(" ")];
  }
  const target = Math.ceil(length(whole) / lines);
  const out = [];
  let line = [];
  ws.forEach((w) => {
    if (line.length && length(`${line.join(" ")} ${w}`) > target && out.length < lines - 1) { out.push(line.join(" ")); line = []; }
    line.push(w);
  });
  out.push(line.join(" "));
  return out;
}

// ------------------------------------------------------------------------------------------ timing against the audio
// activity = { fps, voiced: Uint8Array } (1 where somebody speaks). The stretches of speech between `from` and `to`, with small gaps closed and tiny blips dropped.
export function voicedIntervals(activity, from, to, merge = 0.15, blip = 0.06) {
  if (!activity || !activity.voiced || !activity.voiced.length) return [];
  const { fps, voiced } = activity;
  const a = Math.max(0, Math.floor(from * fps));
  const b = Math.min(voiced.length, Math.ceil(to * fps));
  const raw = [];
  let start = -1;
  for (let i = a; i <= b; i += 1) {
    const on = i < b && voiced[i] === 1;
    if (on && start < 0) start = i;
    if (!on && start >= 0) { raw.push([start / fps, i / fps]); start = -1; }
  }
  const merged = [];
  raw.forEach((iv) => {
    const last = merged[merged.length - 1];
    if (last && iv[0] - last[1] < merge) last[1] = iv[1]; else merged.push([...iv]);
  });
  return merged.filter(([s, e]) => e - s >= blip);
}

// Gives the texts of one recognised piece (spoken between `from` and `to`) their times. Each text gets a share of the time somebody is really speaking, in proportion to its
// length; where two texts meet, the boundary is moved to the nearest pause in the speech when there is one close by.
export function alignTexts(texts, from, to, activity) {
  const n = texts.length;
  if (!n) return [];
  const weights = texts.map((t) => Math.max(1, length(t.replace(/\s+/g, "")) + wordsOf(t).length));
  const total = weights.reduce((a, b) => a + b, 0);
  let intervals = voicedIntervals(activity, from, to);
  if (!intervals.length) intervals = [[from, to]];
  const voicedTotal = intervals.reduce((a, [s, e]) => a + e - s, 0);
  const timeAt = (v) => {
    let left = v;
    for (const [s, e] of intervals) {
      if (left <= e - s) return s + left;
      left -= e - s;
    }
    return intervals[intervals.length - 1][1];
  };
  const gaps = intervals.slice(1).map((iv, i) => [intervals[i][1], iv[0]]).filter((g) => g[1] - g[0] >= 0.12);
  const used = new Set();
  const edges = [];                                                                // for every boundary: where the cue before ends and the cue after starts
  let acc = 0;
  for (let i = 0; i < n - 1; i += 1) {
    acc += weights[i];
    const at = timeAt((acc / total) * voicedTotal);
    let best = -1;
    let bestDistance = 0.7;
    gaps.forEach((g, k) => {
      const d = Math.abs((g[0] + g[1]) / 2 - at);
      if (!used.has(k) && d < bestDistance) { bestDistance = d; best = k; }
    });
    if (best >= 0) {
      used.add(best);
      const [g0, g1] = gaps[best];
      const mid = (g0 + g1) / 2;
      edges.push({ end: Math.min(g0 + 0.1, mid), start: Math.max(g1 - 0.1, mid) });
    } else edges.push({ end: at, start: at });
  }
  const first = Math.max(from, intervals[0][0] - 0.08);
  const last = Math.min(to, intervals[intervals.length - 1][1] + 0.2);
  const cues = texts.map((text, i) => ({ text, start: i === 0 ? first : edges[i - 1].start, end: i === n - 1 ? last : edges[i].end }));
  for (let i = 1; i < cues.length; i += 1) {                                       // snapping can only ever have put a boundary out of order by a little
    if (cues[i].start < cues[i - 1].start + 0.2) cues[i].start = cues[i - 1].start + 0.2;
    if (cues[i - 1].end > cues[i].start) cues[i - 1].end = cues[i].start;
  }
  return cues;
}

// ------------------------------------------------------------------------------------------ the whole list
const fix = (n) => Math.round(n * 1000) / 1000;

// Splits a cue in two at the word nearest to `at` (a time; the middle by default), the time divided in proportion to the text.
export function splitCue(cue, at = null) {
  const ws = wordsOf(cue.text);
  if (ws.length < 2) return [cue];
  const share = at === null ? 0.5 : Math.min(0.9, Math.max(0.1, (at - cue.start) / Math.max(0.01, cue.end - cue.start)));
  const chars = ws.map((w) => length(w) + 1);
  const all = chars.reduce((a, b) => a + b, 0);
  let cut = 1;
  let acc = 0;
  let bestDistance = Infinity;
  for (let i = 1; i < ws.length; i += 1) {
    acc += chars[i - 1];
    const distance = Math.abs(acc / all - share);
    if (distance < bestDistance) { bestDistance = distance; cut = i; }
  }
  const left = ws.slice(0, cut).join(" ");
  const right = ws.slice(cut).join(" ");
  const middle = at === null ? cue.start + ((cue.end - cue.start) * length(left)) / (length(left) + length(right)) : at;
  const altWords = wordsOf(cue.alt);
  const altCut = altWords.length > 1 ? Math.max(1, Math.round((altWords.length * cut) / ws.length)) : 0;
  return [
    { ...cue, text: left, end: fix(middle - 0.02), alt: altCut ? altWords.slice(0, altCut).join(" ") : cue.alt },
    { ...cue, id: newId(), text: right, start: fix(middle + 0.02), alt: altCut ? altWords.slice(altCut).join(" ") : "" },
  ];
}

export function mergeCues(a, b) {
  return { ...a, text: `${a.text} ${b.text}`.trim(), alt: `${a.alt || ""} ${b.alt || ""}`.trim(), start: a.start, end: Math.max(a.end, b.end) };
}

// Puts a list of cues in order and makes it safe to show: no cue shorter than minSeconds when there is room, none overlapping the next, none too long.
export function finalize(list, options = {}) {
  const o = { ...DEFAULTS, ...options };
  let cues = list.filter((c) => c.text && c.text.trim()).map((c) => ({ alt: "", ...c, id: c.id || newId(), text: c.text.trim(), start: fix(c.start), end: fix(Math.max(c.end, c.start + 0.1)) }));
  cues.sort((a, b) => a.start - b.start);
  for (let guard = 0; guard < 2000; guard += 1) {                                  // cues that last too long are split until none does
    const k = cues.findIndex((c) => c.end - c.start > o.maxSeconds && wordsOf(c.text).length > 1);
    if (k < 0) break;
    cues.splice(k, 1, ...splitCue(cues[k]));
  }
  cues = cues.map((c, i) => {
    const next = cues[i + 1];
    const room = next ? next.start - o.gap : Infinity;
    let end = c.end;
    if (end - c.start < o.minSeconds) end = Math.min(c.start + o.minSeconds, Math.max(end, room));
    if (next && end > room) end = Math.max(c.start + 0.2, room);
    return { ...c, end: fix(end) };
  });
  for (let i = 1; i < cues.length; i += 1) {                                       // a cue squeezed to nothing pushes the next one on rather than overlapping it
    if (cues[i].start < cues[i - 1].end) cues[i] = { ...cues[i], start: fix(cues[i - 1].end + o.gap) };
    if (cues[i].end <= cues[i].start) cues[i] = { ...cues[i], end: fix(cues[i].start + 0.3) };
  }
  return cues;
}

// The cues of a whole transcript: `segments` = [{ start, end, text }] (what the recogniser returned for each piece of the recording).
export function buildCues(segments, activity, options = {}) {
  const o = { ...DEFAULTS, ...options };
  const cues = [];
  (segments || []).forEach((seg) => {
    const texts = splitText(seg.text, o);
    alignTexts(texts, seg.start, seg.end, activity).forEach((c) => cues.push(c));
  });
  return finalize(cues, o);
}

// Moves every cue by `seconds` (negative = earlier), never before 0.
export function shiftAll(cues, seconds) {
  return cues.map((c) => ({ ...c, start: fix(Math.max(0, c.start + seconds)), end: fix(Math.max(0.1, c.end + seconds)) }));
}

// The cue shown at time t, or null (cues are sorted).
export function cueAt(cues, t) {
  let lo = 0;
  let hi = cues.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (t < cues[mid].start) hi = mid - 1;
    else if (t >= cues[mid].end) lo = mid + 1;
    else return cues[mid];
  }
  return null;
}

// What is not quite right about a cue (for a small warning in the editor), or "".
export function warningFor(cue, options = {}) {
  const o = { ...DEFAULTS, ...options };
  const seconds = cue.end - cue.start;
  const text = cue.text.replace(/\s+/g, " ");
  if (seconds > o.maxSeconds + 0.01) return "Stays on screen for a long time.";
  if (length(text) / Math.max(0.1, seconds) > o.maxCps + 3) return "Hard to read in the time given.";
  if (wrapLines(text, o.maxChars, o.maxLines).some((l) => length(l) > o.maxChars + 8)) return "A line is long.";
  return "";
}

// ------------------------------------------------------------------------------------------ files
const pad = (n, size = 2) => String(Math.floor(n)).padStart(size, "0");
function clock(t, separator) {
  const total = Math.max(0, Math.round(t * 1000));
  return `${pad(total / 3600000)}:${pad((total % 3600000) / 60000)}:${pad((total % 60000) / 1000)}${separator}${pad(total % 1000, 3)}`;
}
export const srtTime = (t) => clock(t, ",");
export const vttTime = (t) => clock(t, ".");

// The text of a cue for a file: the original, the translation, or both (original first). `lines` breaks it as it is shown.
export function cueText(cue, which = "text", options = {}) {
  const o = { ...DEFAULTS, ...options };
  const one = (t) => wrapLines(t, o.maxChars, o.maxLines).join("\n");
  if (which === "alt") return one(cue.alt || cue.text);
  if (which === "both" && cue.alt) return `${one(cue.text)}\n${one(cue.alt)}`;
  return one(cue.text);
}

export function toSrt(cues, which = "text", options = {}) {
  return `${cues.map((c, i) => `${i + 1}\n${srtTime(c.start)} --> ${srtTime(c.end)}\n${cueText(c, which, options)}\n`).join("\n")}`;
}

export function toVtt(cues, which = "text", options = {}) {
  return `WEBVTT\n\n${cues.map((c) => `${vttTime(c.start)} --> ${vttTime(c.end)}\n${cueText(c, which, options)}\n`).join("\n")}`;
}

export const toTxt = (cues, which = "text") => cues.map((c) => (which === "alt" ? c.alt || c.text : c.text)).join("\n");

const parseClock = (s) => {
  const m = String(s).trim().match(/^(?:(\d+):)?(\d{1,2}):(\d{2})[.,](\d{1,3})$/);
  if (!m) return null;
  return Number(m[1] || 0) * 3600 + Number(m[2]) * 60 + Number(m[3]) + Number(m[4].padEnd(3, "0")) / 1000;
};

// Reads an SRT or WebVTT file into cues (tags such as <i> are removed). Returns [] when there is nothing to read.
export function parseSubtitles(text) {
  const out = [];
  const blocks = String(text || "").replace(/\r/g, "").replace(/^﻿/, "").split(/\n{2,}/);
  blocks.forEach((block) => {
    const lines = block.split("\n").filter((l) => l.trim() !== "");
    const at = lines.findIndex((l) => l.includes("-->"));
    if (at < 0) return;
    const [a, b] = lines[at].split("-->").map((s) => s.trim().split(/\s+/)[0]);
    const start = parseClock(a);
    const end = parseClock(b);
    const body = lines.slice(at + 1).join(" ").replace(/<[^>]+>/g, "").replace(/\{\\[^}]*\}/g, "").replace(/\s+/g, " ").trim();
    if (start !== null && end !== null && end > start && body) out.push({ id: newId(), start, end, text: body, alt: "" });
  });
  return out;
}

// ------------------------------------------------------------------------------------------ typing times
// How a time is shown in the editor: m:ss.mmm (h:mm:ss.mmm from an hour).
export function editTime(t) {
  const total = Math.max(0, Math.round(t * 1000));
  const h = Math.floor(total / 3600000);
  const m = Math.floor((total % 3600000) / 60000);
  const s = Math.floor((total % 60000) / 1000);
  const ms = total % 1000;
  return `${h ? `${h}:${pad(m)}` : m}:${pad(s)}.${pad(ms, 3)}`;
}

// What the visitor typed as a time: "83.5", "1:23.5", "1:23,500", "0:01:23.5". Returns null when it is not a time.
export function readTime(text) {
  const s = String(text || "").trim().replace(",", ".");
  if (/^\d+(\.\d+)?$/.test(s)) return Number(s);
  const parts = s.split(":");
  if (parts.length < 2 || parts.length > 3 || parts.some((p) => !/^\d+(\.\d+)?$/.test(p))) return null;
  const nums = parts.map(Number);
  return nums.length === 3 ? nums[0] * 3600 + nums[1] * 60 + nums[2] : nums[0] * 60 + nums[1];
}
